import type { EmployeeCompanyResolution } from "@/utils/functions/employeeImport";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAutoRefresh } from "../BaseQuery";
import { ApiResponse } from "../../types/apiTypes";
import { vipApi } from "./vipApi";

export interface CompanyEmployee {
  employee_id?: string;
  company_id?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  job_title?: string;
  role_category?: string;
  role_tags?: string[];
  department?: string;
  email?: string;
  mobile_phone?: string;
  linkedin_url?: string;
  created_at?: Date;
  updated_at?: Date;
}

export interface CompanyInfo {
  company_name: string;
  company_website?: string;
  market_value_thousands?: number;
  company_industry?: string;
  company_facebook_url?: string;
  company_employee_count?: number;
  published?: boolean;
  logo?: string;
}

export interface EmployeeWithCompany extends CompanyEmployee {
  company_info?: CompanyInfo;
}

export interface EmployeeCompanyPageData {
  company_id: string;
  company_info?: CompanyInfo;
  selected_employee_id: string;
  employees: EmployeeWithCompany[];
}

export const employeeApi = createApi({
  reducerPath: "employeeApi",
  baseQuery: baseQueryWithAutoRefresh,
  tagTypes: ["Employee", "token", "my-hr"],
  endpoints: (builder) => ({
    resolveEmployeeCompanies: builder.mutation<ApiResponse<Omit<EmployeeCompanyResolution, "choice">[]>, Record<string, any>[]>({
      query: employees => ({ url: "/admin/employee/resolve-companies", method: "POST", body: { employees } }),
    }),
    // Create a new employee
    createEmployee: builder.mutation<ApiResponse<{ id: string; message: string }>, CompanyEmployee>({
      query: (newEmployee) => ({
        url: "/employee/create",
        method: "POST",
        body: newEmployee,
      }),
      invalidatesTags: ["Employee"],
    }),

    // Bulk create employees
    bulkCreateEmployees: builder.mutation<
      ApiResponse<{ ids: string[]; message: string; count: number }>,
      CompanyEmployee[]
    >({
      query: (employees) => ({
        url: "/employee/bulk-create",
        method: "POST",
        body: { employees },
      }),
      invalidatesTags: ["Employee"],
    }),

    // Get employee by ID
    getEmployeeById: builder.query<ApiResponse<EmployeeWithCompany>, string>({
      providesTags: ["Employee", "my-hr"],
      query: (id) => ({
        url: `/employee/get/${id}`,
        method: "GET",
      }),
      transformResponse: (response: ApiResponse<any>) => {
        // Map _id to employee_id if needed
        if (response.data && response.data._id && !response.data.employee_id) {
          response.data.employee_id = response.data._id;
        }
        return response;
      },
    }),

    getEmployeeCompanyPage: builder.query<ApiResponse<EmployeeCompanyPageData>, string>({
      providesTags: ["Employee", "my-hr"],
      query: (id) => ({
        url: `/employee/company-page/${id}`,
        method: "GET",
      }),
      transformResponse: (response: ApiResponse<any>) => {
        if (response.data?.employees && Array.isArray(response.data.employees)) {
          response.data.employees = response.data.employees.map((employee: any) => ({
            ...employee,
            employee_id: employee._id || employee.employee_id,
          }));
        }
        return response;
      },
    }),

    // Update employee
    updateEmployee: builder.mutation<ApiResponse<{ message: string }>, { id: string; updates: Partial<CompanyEmployee> }>({
      query: ({ id, updates }) => ({
        url: `/employee/update/${id}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: ["Employee"],
    }),

    // Bulk update employees
    bulkUpdateEmployees: builder.mutation<
      ApiResponse<{ message: string }>,
      Array<{ id: string; doc: Partial<CompanyEmployee> }>
    >({
      query: (updates) => ({
        url: "/employee/bulk-update",
        method: "PUT",
        body: { updates },
      }),
      invalidatesTags: ["Employee"],
    }),

    // Delete employee
    deleteEmployee: builder.mutation<ApiResponse<{ message: string }>, string>({
      query: (id) => ({
        url: `/employee/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Employee"],
    }),

    // Search employees
    searchEmployees: builder.mutation<
      ApiResponse<any>,
      {
        query?: any;
        searchTerm?: string;
        queryText?: string;
        companyName?: string;
        size?: number;
        from?: number;
        page?: number;
        limit?: number;
        sortBy?: string;
        sort_by?: string;
      }
    >({
      query: (searchParams) => ({
        url: "/employee/search",
        method: "POST",
        body: searchParams,
      }),
      transformResponse: (response: ApiResponse<any>) => {
        // Map _id to employee_id for each result
        if (response.data && Array.isArray(response.data)) {
          response.data = response.data.map((employee: any) => ({
            ...employee,
            employee_id: employee._id || employee.employee_id,
          }));
        }
        return response;
      },
    }),

    // Buy HR employee (unlock phone/email)
    buyHREmployee: builder.mutation<ApiResponse<EmployeeWithCompany>, { employeeId: string }>({
      invalidatesTags: ["Employee", "token", "my-hr"],
      query: ({ employeeId }) => ({
        url: `/employee/buy/${employeeId}`,
        method: "POST",
      }),
      transformResponse: (response: ApiResponse<any>) => {
        // Map _id to employee_id if needed
        if (response.data && response.data._id && !response.data.employee_id) {
          response.data.employee_id = response.data._id;
        }
        return response;
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(vipApi.util.invalidateTags(["token"]));
        } catch {
          // Keep the existing mutation error path responsible for user-facing handling.
        }
      },
    }),

    // Get my HR employees (purchased)
    getMyHREmployees: builder.query<ApiResponse<EmployeeWithCompany[]>, void>({
      providesTags: ["my-hr"],
      query: () => ({
        url: "/employee/my-hr",
        method: "GET",
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          const { setHREmployees } = await import("../reducers/hrSlice");
          dispatch(setHREmployees(response.data));
        } catch {
          const { clearHREmployees } = await import("../reducers/hrSlice");
          dispatch(clearHREmployees());
        }
      },
    }),
  }),
});

export const {
  useResolveEmployeeCompaniesMutation,
  useCreateEmployeeMutation,
  useBulkCreateEmployeesMutation,
  useGetEmployeeByIdQuery,
  useGetEmployeeCompanyPageQuery,
  useLazyGetEmployeeCompanyPageQuery,
  useLazyGetEmployeeByIdQuery,
  useUpdateEmployeeMutation,
  useBulkUpdateEmployeesMutation,
  useDeleteEmployeeMutation,
  useSearchEmployeesMutation,
  useBuyHREmployeeMutation,
  useGetMyHREmployeesQuery,
} = employeeApi;
